import React, { useState, useEffect } from 'react';
import { apiFetch } from '../../../../lib/api';
import { API_DOCS } from './TabDashboard.api';
import ApiInspector from '../../../../components/ApiInspector';

interface Nominativo {
  id: number;
  codice: string;
  tipoSoggetto: 'AZIENDA' | 'PRIVATO';
  ragioneSociale?: string;
  nome?: string;
  cognome?: string;
  partitaIva?: string;
  codiceFiscale?: string;
  codiceSDI?: string;
  pec?: string;
  telefono?: string;
  emailPrincipale?: string;
  sito?: string;
  isCliente: boolean;
  isFornitore: boolean;
  disattivato: boolean;
  bloccato: boolean;
  societa?: {
    id: number;
    nome: string;
  };
  indirizzi?: Array<{
    id: number;
    tipo: 'SEDE_LEGALE' | 'SEDE_OPERATIVA' | 'SPEDIZIONE';
    nome?: string;
    via?: string;
    cap?: string;
    citta?: string;
    provincia?: string;
    paese?: string;
    principale: boolean;
  }>;
}

interface TabDashboardProps {
  nominativoId: number;
}

export default function TabDashboard({ nominativoId }: TabDashboardProps) {
  const [nominativo, setNominativo] = useState<Nominativo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadNominativo();
  }, [nominativoId]);

  const loadNominativo = async () => {
    try {
      setLoading(true);
      // @api GET /api/nominativi/:id -> {"id":"number","codice":"string","ragioneSociale":"string","nome":"string","cognome":"string","tipoSoggetto":"string","partitaIva":"string","codiceFiscale":"string","codiceSDI":"string","pec":"string","telefono":"string","emailPrincipale":"string","sito":"string","isCliente":"boolean","isFornitore":"boolean","disattivato":"boolean","bloccato":"boolean","societa":"object","indirizzi":"array"}
      const data = await apiFetch<Nominativo>(`/api/nominativi/${nominativoId}`);
      setNominativo(data);
      setError('');
    } catch (err: any) {
      console.error('Errore caricamento nominativo:', err);
      setError(err.message || 'Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  // Formatta la sede legale (indirizzo con tipo SEDE_LEGALE) in una stringa leggibile
  const formatSedeLegale = (): string | null => {
    const sede = nominativo?.indirizzi?.find(i => i.tipo === 'SEDE_LEGALE');
    if (!sede) return null;
    const riga1 = sede.via?.trim() || '';
    const localita = [sede.cap, sede.citta].filter(Boolean).join(' ');
    const provincia = sede.provincia ? `(${sede.provincia})` : '';
    const riga2 = [localita, provincia].filter(Boolean).join(' ');
    const parti = [riga1, riga2, sede.paese?.trim()].filter(Boolean);
    return parti.length > 0 ? parti.join(' · ') : null;
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <p className="bop-text-muted">Caricamento...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <p className="bop-text-danger">{error}</p>
      </div>
    );
  }

  if (!nominativo) {
    return (
      <div className="dashboard-empty">
        <p className="bop-text-muted">Nessun dato disponibile</p>
      </div>
    );
  }

  return (
    <div className="tab-dashboard">
      {/* Dati Principali */}
      <div className="bop-card">
        <h3 className="dashboard-section-title">Dati Principali</h3>
        <div className="dashboard-data-grid">
          <div className="dashboard-field">
            <span className="bop-label">Partita IVA</span>
            <span className="dashboard-value">{nominativo.partitaIva || '—'}</span>
          </div>
          <div className="dashboard-field">
            <span className="bop-label">Codice Fiscale</span>
            <span className="dashboard-value">{nominativo.codiceFiscale || '—'}</span>
          </div>
          <div className="dashboard-field">
            <span className="bop-label">Codice SDI</span>
            <span className="dashboard-value">{nominativo.codiceSDI || '—'}</span>
          </div>
          <div className="dashboard-field">
            <span className="bop-label">PEC</span>
            <span className="dashboard-value">{nominativo.pec || '—'}</span>
          </div>
          <div className="dashboard-field">
            <span className="bop-label">Telefono Principale</span>
            <span className="dashboard-value">{nominativo.telefono || '—'}</span>
          </div>
          <div className="dashboard-field">
            <span className="bop-label">Email Principale</span>
            <span className="dashboard-value">{nominativo.emailPrincipale || '—'}</span>
          </div>
          <div className="dashboard-field dashboard-field-full">
            <span className="bop-label">Sede Legale</span>
            {formatSedeLegale() ? (
              <span className="dashboard-value">{formatSedeLegale()}</span>
            ) : (
              <span className="dashboard-value-missing">
                ⚠️ Sede legale non codificata
              </span>
            )}
          </div>
          {nominativo.sito && (
            <div className="dashboard-field dashboard-field-full">
              <span className="bop-label">Sito Web</span>
              <a href={nominativo.sito} target="_blank" rel="noopener noreferrer" className="dashboard-link">
                {nominativo.sito}
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Ruoli */}
      <div className="bop-card">
        <h3 className="dashboard-section-title">Ruoli</h3>
        <div className="dashboard-ruoli">
          {nominativo.isCliente && (
            <div className="dashboard-ruolo-card dashboard-ruolo-cliente">
              <div className="dashboard-ruolo-icon">👤</div>
              <div className="dashboard-ruolo-label">Cliente</div>
            </div>
          )}
          {nominativo.isFornitore && (
            <div className="dashboard-ruolo-card dashboard-ruolo-fornitore">
              <div className="dashboard-ruolo-icon">🏭</div>
              <div className="dashboard-ruolo-label">Fornitore</div>
            </div>
          )}
          {!nominativo.isCliente && !nominativo.isFornitore && (
            <p className="bop-text-muted">Nessun ruolo assegnato</p>
          )}
        </div>
      </div>

      {/* Statistiche Placeholder */}
      <div className="bop-card dashboard-stats-placeholder">
        <h3 className="dashboard-section-title">Statistiche</h3>
        <div className="dashboard-placeholder-content">
          <div className="dashboard-placeholder-icon">📊</div>
          <p className="dashboard-placeholder-text">
            Statistiche ciclo attivo/passivo — disponibili prossimamente
          </p>
        </div>
      </div>

      <ApiInspector docs={API_DOCS} />
    </div>
  );
}
